import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowRight, ArrowLeft, KeyRound } from 'lucide-react';
import { API } from '../lib/constants';

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const SignIn = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const requestCode = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/request-code`, { email }, { withCredentials: true });
      setSent(true);
      toast.success('If an account exists, a 6-digit code is on its way.');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/verify-code`, { email, code }, { withCredentials: true });
      if (res.data.success) {
        localStorage.setItem('userId', res.data.user_id);
        localStorage.setItem('userEmail', res.data.email);
        if (res.data.username) localStorage.setItem('username', res.data.username);
        if (res.data.is_admin) localStorage.setItem('isAdmin', 'true');
        else localStorage.removeItem('isAdmin');
        toast.success('Welcome back!');
        if (res.data.user_type === 'college_coach' || res.data.user_type === 'hs_coach') { navigate('/coach-dashboard'); return; }
        navigate(res.data.is_admin && !res.data.username ? '/admin' : '/dashboard');
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/offersz-lockup.png" alt="Offersz" data-testid="signin-wordmark" className="w-40 h-auto mx-auto mb-6" />
          <h1 className="text-3xl font-black mb-2">Welcome back</h1>
          <p className="text-white/50">
            {sent ? 'Enter the 6-digit code we emailed you.' : 'Sign in with a one-time code — no password needed.'}
          </p>
        </div>

        <div className="space-y-4">
          {!sent ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email
                </label>
                <Input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  type="email"
                  placeholder="you@email.com"
                  data-testid="signin-email-input"
                  onKeyPress={e => e.key === 'Enter' && requestCode()}
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                />
              </div>

              <Button
                onClick={requestCode}
                disabled={!email || loading}
                data-testid="signin-request-code-btn"
                className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg"
              >
                {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ArrowRight className="mr-2 h-5 w-5" />}
                Email me a code
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                  <KeyRound className="h-4 w-4" /> 6-digit code
                </label>
                <Input
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  data-testid="signin-code-input"
                  onKeyPress={e => e.key === 'Enter' && verifyCode()}
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl text-center text-2xl tracking-[0.5em] font-bold"
                />
                <p className="text-white/30 text-xs">Sent to {email}. The code expires in 24 hours.</p>
              </div>

              <Button
                onClick={verifyCode}
                disabled={code.length !== 6 || loading}
                data-testid="signin-verify-code-btn"
                className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg"
              >
                {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ArrowRight className="mr-2 h-5 w-5" />}
                Verify & sign in
              </Button>

              <button onClick={requestCode} disabled={loading} data-testid="signin-resend-code-btn" className="w-full text-white/40 hover:text-white/70 text-sm py-1">
                Resend code
              </button>
              <button onClick={() => { setSent(false); setCode(''); }} className="w-full text-white/30 hover:text-white/50 text-sm py-1">
                Use a different email
              </button>
            </>
          )}

          <div className="text-center pt-4">
            <p className="text-white/30 text-sm">
              Don't have a profile?{' '}
              <button onClick={() => navigate('/create-profile')} data-testid="signin-create-link" className="text-[#16A34A] hover:text-[#16A34A] font-semibold">
                Create one free
              </button>
            </p>
          </div>

          <button onClick={() => navigate('/')} className="w-full text-white/20 text-sm py-2 hover:text-white/40 flex items-center justify-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
